import request from 'sb-request'
import graphqlRequest from 'sb-request/graphql'
import { useConnect } from 'sb-redux-hook'
import type { ModalVisibilityProps } from 'sb-modal'
import { useReducerState, useCallback, useEffect } from 'sb-hooks'
import { formatPrice, snowplow, object } from 'common/helpers'
import subscriptionActions from 'redux/models/subscription/actions'
import uiActions from 'redux/models/ui/actions'
import type { DowngradePlansPayload } from 'actions/subscription/downgradePlansGQL'

import { openDowngradeSuccessModal } from 'compositions/modals/cancelSubscription'

import plansQuery from './plans.graphql'
import recurlyDowngradePlansQuery from './recurlyDowngradePlans.graphql'


type DowngradePlansResult = {
  data: DowngradePlansPayload['currentUser']['data']['subscription']['changePlansInquiry']['downgradePlans']
}

export const getDowngradePlans = (): Promise<DowngradePlansResult> =>
  graphqlRequest<never, DowngradePlansPayload, DowngradePlansResult>('getDowngradePlans', {
    query: recurlyDowngradePlansQuery,
  }, {
    modifyResult: ({ currentUser: { data } }) => ({
      data: object.get(data, 'subscription.changePlansInquiry.downgradePlans'),
    }),
  })

export type UseDowngradeModalProps = DowngradeModal.ModalProps & ModalVisibilityProps

const useDowngradeModal = (props: UseDowngradeModalProps): DowngradeModal.ReturnType => {
  const { closeModal, openNextModal, onClose } = props

  const { paymentProvider, nextBillingDate, productsPerPeriod } = useConnect({
    paymentProvider: 'models.me.paymentProvider',
    nextBillingDate: 'models.subscription.nextBillingDate',
    productsPerPeriod: 'models.subscription.currPlan.productsPerPeriod',
  })

  const [ state, setState ] = useReducerState({ isFetching: true, plans: null, selectedPlan: productsPerPeriod })

  const { isFetching, plans, selectedPlan } = state

  useEffect(() => {
    snowplow.track('UF downgrade popup')

    if (paymentProvider === 'RECURLY') {
      getDowngradePlans()
        .then(({ data }) => {
          const result = data
            .filter(({ plan }) => (
              plan.productsPerPeriod <= productsPerPeriod
            ))
            .map(({ plan }) => ({
              ...plan,
              price: formatPrice(plan.price, true),
            }))

          setState({ isFetching: false, plans: result })
        })
    }
    else {
      request.gql('downgradePlans', {
        body: {
          query: plansQuery,
          variables: {
            input: {
              billing: 1,
            },
          },
        },
        modifyResult: ({ plans }) => (
          plans
            .filter((plan) => plan.productsPerPeriod <= productsPerPeriod)
            .map(({ priceCents, ...plan }) => ({
              ...plan,
              price: formatPrice(priceCents, true),
            }))
        ),
      })
        .then((result) => {
          setState({ isFetching: false, plans: result })
        })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const setSelectedPlan = useCallback((plan) => {
    setState({ selectedPlan: plan })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const onConfirm = useCallback(() => {
    const { name } = plans.find(({ productsPerPeriod }) => selectedPlan === productsPerPeriod)

    uiActions.showRequestLoader()

    subscriptionActions.changePlan(name, 'uf downgrade popup')
      .then(
        () => {
          closeModal()
          openDowngradeSuccessModal()
        },
        () => {}
      )
      .finally(() => {
        uiActions.hideRequestLoader()
      })
  }, [ plans, selectedPlan, closeModal ])

  const onContinue = useCallback(() => {
    closeModal()
    openNextModal()
  }, [ closeModal, openNextModal ])

  return {
    plans,
    isFetching,
    selectedPlan,
    nextBillingDate,
    productsPerPeriod,
    setSelectedPlan,
    onContinue,
    onConfirm,
    onClose,
  }
}


export default useDowngradeModal
